import { takeLatest, call, put } from 'redux-saga/effects';
import { SendApiRequest } from 'src/api';
import { apiRoutes } from 'src/api/routes';
import {
    fetchCommandCentre,
    commandCentreFetched,
    commandCentreFetchFailed,
    dispatchCohortRequested,
    dispatchCohortSucceeded,
    dispatchCohortFailed,
    optimisticWaveSent,
} from '../commandCentre';
import { showToastAction } from '../ui';

function* fetchCommandCentreSaga(action) {
    try {
        const {
            jobId,
            page = 1,
            pageSize = 20,
            refresh = false,
        } = action.payload || {};
        const url = apiRoutes.shiftFillCommandCentre(jobId, {
            page,
            pageSize,
            refresh,
        });
        const data = yield call(SendApiRequest, { method: 'GET', url });
        yield put(commandCentreFetched(data));
    } catch (e) {
        const message =
            (e && e.response && e.response.data && e.response.data.message) ||
            (e && e.message) ||
            'Failed to load Command Centre';
        yield put(commandCentreFetchFailed(message));
        yield put(showToastAction({ type: 'danger', message }));
    }
}

function humanizeDispatchError(rawMessage) {
    if (!rawMessage) return 'Failed to dispatch wave';
    if (rawMessage === 'WAVE_ALREADY_DISPATCHED') {
        return 'This wave has already been sent. Refreshing to sync.';
    }
    if (rawMessage === 'WAVE_EMPTY_AFTER_EXCLUSIONS') {
        return 'No recipients left after exclusions. Refreshing to sync.';
    }
    if (rawMessage === 'JOB_NOT_FOUND') {
        return 'Shift not found.';
    }
    return rawMessage;
}

function* dispatchCohortSaga(action) {
    const { jobId, wave, excludeIds } = action.payload || {};
    try {
        const url = apiRoutes.shiftFillDispatchCohort(jobId);
        const data = yield call(SendApiRequest, {
            method: 'POST',
            url,
            body: { wave, excludeIds: excludeIds || [] },
        });

        // Optimistic local update so the wave card flips to "sent" immediately.
        // The refetch a moment later will replace this with authoritative data.
        yield put(
            optimisticWaveSent({
                wave,
                excludeIds: excludeIds || [],
                dispatchedAt: new Date().toISOString(),
            }),
        );
        yield put(
            dispatchCohortSucceeded({
                wave,
                dispatched: data && data.dispatched,
                windowExpiresAt: data && data.windowExpiresAt,
            }),
        );
        yield put(
            showToastAction({
                type: 'success',
                message: `Wave ${wave} sent to ${
                    (data && data.dispatched) || 0
                } contractor${(data && data.dispatched) === 1 ? '' : 's'}.`,
            }),
        );

        // Refetch to refresh the rest of the cohort grid (later waves now
        // exclude the just-dispatched contractors).
        yield put(fetchCommandCentre({ jobId, refresh: true }));
    } catch (e) {
        const rawMessage = (e && e.message) || 'Failed to dispatch wave';
        const userMessage = humanizeDispatchError(rawMessage);
        yield put(dispatchCohortFailed({ wave, error: rawMessage }));
        yield put(showToastAction({ type: 'danger', message: userMessage }));

        // Re-sync on error so the panel reflects authoritative server state.
        if (jobId) {
            yield put(fetchCommandCentre({ jobId, refresh: true }));
        }
    }
}

export const commandCentreSagas = [
    takeLatest(fetchCommandCentre.type, fetchCommandCentreSaga),
    takeLatest(dispatchCohortRequested.type, dispatchCohortSaga),
];
